import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { listAdminChanges } from '../../../services/adminApi';
import { formatEuros } from './productForm';

// Historique des modifications faites depuis l'Admin : qui, quand, et pour chaque champ l'ancienne et la nouvelle valeur.
// Alimente par GET /api/admin/changes (table admin_changes). Une panne de lecture n'empeche rien : on le dit, on n'invente pas.

const estMontant = (champ) => /cents$/i.test(champ);

export function libelleChamp(t, champ) {
  const morceaux = String(champ).split('.');
  if (morceaux[0] === 'shipping' && morceaux.length >= 3) {
    return `${t('catalog.history.fields.shipping')} ${morceaux[1]} · ${t(`catalog.history.fields.${morceaux[2] === 'costCents' ? 'shippingCost' : 'shippingPrice'}`)}`;
  }
  if (morceaux[0] === 'formats' && morceaux.length >= 3) {
    return `${t(`catalog.history.formats.${morceaux[1]}`, { defaultValue: morceaux[1] })} · ${t(`catalog.history.fields.${morceaux[2]}`, { defaultValue: morceaux[2] })}`;
  }
  if (morceaux[0] === 'extra' && morceaux[1] === 'photos') return `${t('catalog.history.fields.photo')} · ${t(`catalog.drawer.photoSlots.${morceaux[2]}`, { defaultValue: morceaux[2] })}`;
  return t(`catalog.history.fields.${champ}`, { defaultValue: champ });
}

export function formaterValeur(t, champ, valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return '—';
  if (typeof valeur === 'boolean') return valeur ? t('catalog.common.yes') : t('catalog.common.no');
  if (estMontant(champ) && Number.isFinite(Number(valeur))) return formatEuros(Number(valeur));
  const texte = typeof valeur === 'object' ? JSON.stringify(valeur) : String(valeur);
  return texte.length > 70 ? `${texte.slice(0, 67)}…` : texte;
}

export default function ChangeHistory({ area, keyId, limit = 30 }) {
  const { t, i18n } = useTranslation('admin');
  const [lignes, setLignes] = useState(null);
  const [erreur, setErreur] = useState(false);

  useEffect(() => {
    let actif = true;
    setLignes(null);
    setErreur(false);
    listAdminChanges({ area, key: keyId, limit })
      .then((r) => { if (actif) setLignes(r.changes || []); })
      .catch(() => { if (actif) setErreur(true); });
    return () => { actif = false; };
  }, [area, keyId, limit]);

  if (erreur) return <p className="adm-note" role="status">{t('catalog.history.error')}</p>;
  if (lignes === null) return <p className="adm-note" role="status">{t('catalog.common.loading')}</p>;
  if (lignes.length === 0) return <p className="adm-note">{t('catalog.history.empty')}</p>;

  return (
    <ul className="adm-history">
      {lignes.map((ligne) => (
        <li key={ligne.id}>
          <div className="adm-history-head">
            <b>{t(`catalog.history.actions.${ligne.action}`, { defaultValue: ligne.action })}</b>
            {!keyId && ligne.key && <span className="adm-history-key">{t(`catalog.history.areas.${ligne.area}`, { defaultValue: ligne.area })} · {ligne.key}</span>}
            <span className="adm-note">{new Date(ligne.at).toLocaleString(i18n.language)} · {ligne.actor || t('catalog.history.unknownActor')}</span>
          </div>
          <ul className="adm-history-changes">
            {Object.entries(ligne.changes || {}).slice(0, 12).map(([champ, valeur]) => {
              // Forme attendue [avant, apres] ; toleree aussi { avant, apres } ou une simple valeur (ex. une raison ecrite par une
              // migration SQL) — une ligne d'historique mal formee ne doit JAMAIS faire tomber toute la page (2026-10-23).
              const paire = Array.isArray(valeur) ? valeur : (valeur && typeof valeur === 'object' && ('avant' in valeur || 'apres' in valeur) ? [valeur.avant, valeur.apres] : null);
              return (
                <li key={champ}>
                  <span>{libelleChamp(t, champ)}</span>
                  {paire
                    ? <span className="adm-history-val"><s>{formaterValeur(t, champ, paire[0])}</s> → <b>{formaterValeur(t, champ, paire[1])}</b></span>
                    : <span className="adm-history-val">{typeof valeur === 'string' ? valeur : JSON.stringify(valeur)}</span>}
                </li>
              );
            })}
          </ul>
        </li>
      ))}
    </ul>
  );
}
